/**
 * Notebook shell layout — permanent Hero + theme tabs, content in active tab only.
 *
 * LEGACY (commented in NotebookShell, not deleted):
 * - Flattening all theme tabs' sections into one scroll list
 * - AddSectionInline at the bottom of the global section stack
 * - block_tabs as an inline movable section (inner panels → use theme tabs + active tab sections)
 */

import { resolveBlockType } from '../registry/blocks-registry'
import { HERO_BLOCK_DEFAULTS } from '../Blocks/shared/defaults/block-seeds'
import { normalizeTabsBlockData } from '../models/tabs-model'

export const STRUCTURAL_SECTION_TYPES = new Set(['block_hero', 'block_tabs'])

export function isStructuralSectionType(type) {
  return STRUCTURAL_SECTION_TYPES.has(resolveBlockType(type))
}

/** Prevent duplicate React keys / DOM reuse when migration left twin section rows. */
export function dedupeContentSections(sections) {
  if (!Array.isArray(sections) || sections.length === 0) return []
  const seen = new Set()
  const out = []
  for (const section of sections) {
    const id = section?.id
    if (!id || seen.has(id)) continue
    seen.add(id)
    out.push(section)
  }
  return out
}

/** First hero section in the notebook (any theme tab). */
export function findHeroSection(notebook) {
  if (!notebook?.tabs) return null
  for (const tab of notebook.tabs) {
    const section = tab.sections.find(
      (s) => resolveBlockType(s.type) === 'block_hero',
    )
    if (section) return { section, tabId: tab.id }
  }
  return null
}

/**
 * Content sections for the active theme tab — excludes structural blocks.
 * Lifts blocks from legacy block_tabs when that is the only content (migration read path).
 */
export function getActiveTabContentSections(notebook, activeTabId) {
  const tab =
    notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0]
  if (!tab) return []

  const regular = tab.sections.filter(
    (s) => !isStructuralSectionType(s.type),
  )

  if (regular.length > 0) {
    return dedupeContentSections(
      [...regular].sort((a, b) =>
        (a.sort_key ?? '') < (b.sort_key ?? '') ? -1 : 1,
      ),
    )
  }

  const tabsBlock = tab.sections.find(
    (s) => resolveBlockType(s.type) === 'block_tabs',
  )
  if (!tabsBlock) return []

  const tabsData = normalizeTabsBlockData(tabsBlock.data)
  const innerTab =
    tabsData.tabs.find((t) => t.id === tabsData.activeTab) ?? tabsData.tabs[0]
  if (!innerTab?.blocks?.length) return []

  return dedupeContentSections(
    [...innerTab.blocks]
      .map((b) => ({
        ...b,
        type: resolveBlockType(b.type),
        _legacyInner: {
          tabsSectionId: tabsBlock.id,
          innerTabId: innerTab.id,
        },
      }))
      .sort((a, b) =>
        (a.sort_key ?? '') < (b.sort_key ?? '') ? -1 : 1,
      ),
  )
}

/** Legacy block_tabs inner panel — delete via removeTabBlock, not notebook_sections. */
export function findLegacyInnerBlockLocation(notebook, activeTabId, blockId) {
  const tab =
    notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0]
  if (!tab) return null

  const regular = tab.sections.filter(
    (s) => !isStructuralSectionType(s.type),
  )
  if (regular.length > 0) return null

  const tabsBlock = tab.sections.find(
    (s) => resolveBlockType(s.type) === 'block_tabs',
  )
  if (!tabsBlock) return null

  const tabsData = normalizeTabsBlockData(tabsBlock.data)
  const innerTab =
    tabsData.tabs.find((t) => t.id === tabsData.activeTab) ?? tabsData.tabs[0]
  if (!innerTab?.blocks?.some((b) => b.id === blockId)) return null

  return {
    tabsSectionId: tabsBlock.id,
    innerTabId: innerTab.id,
    blockId,
  }
}

export function heroDataForShell(notebook) {
  const found = findHeroSection(notebook)
  return found?.section?.data ?? { ...HERO_BLOCK_DEFAULTS }
}

export function heroSectionId(notebook) {
  return findHeroSection(notebook)?.section?.id ?? null
}

/** First block_tabs section — prefers active theme tab, then any tab. */
export function findTabsSection(notebook, activeTabId) {
  if (!notebook?.tabs) return null
  const preferred =
    notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0]
  const inPreferred = preferred?.sections.find(
    (s) => resolveBlockType(s.type) === 'block_tabs',
  )
  if (inPreferred) return { section: inPreferred, tabId: preferred.id }
  for (const tab of notebook.tabs) {
    const section = tab.sections.find(
      (s) => resolveBlockType(s.type) === 'block_tabs',
    )
    if (section) return { section, tabId: tab.id }
  }
  return null
}
